import { DICTIONARY_QUERY_PARAMS } from './query-state.js';
import { carryDictionaryDrafts } from './row-controller.js';
import { canAnimateElement, readCssTimeMs } from '../../components/motion-policy.js';
import { cancelAnimations, playAnimation, settleAnimations } from '../../components/motion-runner.js';

const SEARCH_DEBOUNCE_MS = 250;
export const DIRTY_SEARCH_FEEDBACK = 'Save or revert changes before searching.';

function hasDirtyRow(section) {
    return section?.querySelector?.('[data-dictionary-state="dirty"]') !== null;
}

export function replaceDictionaryResults(currentSection, replacementSection) {
    const currentEntries = currentSection.querySelector('[data-dictionary-entries]');
    const replacementEntries = replacementSection.querySelector('[data-dictionary-entries]');
    const currentSummary = currentSection.querySelector('[data-dictionary-search-summary]');
    const replacementSummary = replacementSection.querySelector('[data-dictionary-search-summary]');
    const currentCount = currentSection.querySelector('[data-dictionary-count]');
    const replacementCount = replacementSection.querySelector('[data-dictionary-count]');
    if (
        !(currentEntries instanceof HTMLElement)
        || !(replacementEntries instanceof HTMLElement)
        || !(currentSummary instanceof HTMLElement)
        || !(replacementSummary instanceof HTMLElement)
        || !(currentCount instanceof HTMLElement)
        || !(replacementCount instanceof HTMLElement)
    ) {
        return false;
    }

    carryDictionaryDrafts(currentEntries, replacementEntries);
    currentEntries.replaceWith(replacementEntries);
    currentSummary.textContent = replacementSummary.textContent;
    currentCount.textContent = replacementCount.textContent;

    return true;
}

export function createDictionarySearchController({
    section,
    fetchImpl = globalThis.fetch,
    windowRef = globalThis.window,
    parseSection = (html, type) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');

        return parsed.querySelector(`[data-dictionary-section][data-dictionary-type="${type}"]`);
    },
}) {
    const form = section?.querySelector?.('[data-dictionary-search]');
    const input = form?.querySelector?.('[data-dictionary-search-input]');
    const clearButton = form?.querySelector?.('[data-dictionary-search-clear]');
    const feedback = section?.querySelector?.('[data-dictionary-search-feedback]');
    const type = String(section?.dataset?.dictionaryType ?? '');
    const queryParam = `${type}_q`;
    const fragmentUrl = String(section?.dataset?.dictionaryFragmentUrl ?? '');
    if (
        !(section instanceof HTMLElement)
        || !(form instanceof HTMLFormElement)
        || !(input instanceof HTMLInputElement)
        || !(clearButton instanceof HTMLButtonElement)
        || !(feedback instanceof HTMLElement)
        || !DICTIONARY_QUERY_PARAMS.includes(queryParam)
        || fragmentUrl === ''
        || typeof fetchImpl !== 'function'
        || typeof windowRef?.location?.href !== 'string'
    ) {
        return null;
    }

    let activeController = null;
    let debounceTimer = null;
    let entriesAnimation = null;
    let feedbackAnimation = null;
    let panelAnimation = null;
    let statusAnimations = [];
    let revision = 0;

    const resetPanelMotion = () => {
        cancelAnimations([panelAnimation]);
        panelAnimation = null;
        section.classList.remove('hardware-platforms-panel-resizing');
    };

    const animatePanelMutation = (mutate) => {
        const shouldAnimate = canAnimateElement(section, windowRef);
        const previousHeight = shouldAnimate ? section.getBoundingClientRect().height : 0;
        resetPanelMotion();
        mutate();
        const nextHeight = shouldAnimate ? section.getBoundingClientRect().height : 0;
        if (
            previousHeight <= 0
            || nextHeight <= 0
            || Math.abs(previousHeight - nextHeight) < 1
        ) {
            return null;
        }

        section.classList.add('hardware-platforms-panel-resizing');
        panelAnimation = playAnimation(section, [
            { height: `${previousHeight}px` },
            { height: `${nextHeight}px` },
        ], {
            duration: readCssTimeMs(
                section,
                '--dictionary-panel-resize-duration',
                240,
            ),
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            fill: 'both',
        });

        return panelAnimation;
    };

    const setFeedback = (message = '', animate = false) => {
        cancelAnimations([feedbackAnimation]);
        feedbackAnimation = null;
        feedback.textContent = message;
        feedback.hidden = message === '';
        if (animate && message !== '' && canAnimateElement(feedback, windowRef)) {
            feedbackAnimation = playAnimation(feedback, [
                { opacity: 0, transform: 'translateY(-4px)' },
                { opacity: 1, transform: 'translateY(0)' },
            ], {
                duration: readCssTimeMs(
                    section,
                    '--dictionary-results-swap-duration',
                    160,
                ),
                easing: 'ease-out',
                fill: 'both',
            });
        }
    };

    const updateFeedback = (message = '', animate = false) => {
        const feedbackPanelAnimation = animatePanelMutation(() => {
            setFeedback(message, animate);
        });
        if (feedbackPanelAnimation !== null) {
            void settleAnimations([feedbackPanelAnimation]).then(() => {
                if (panelAnimation === feedbackPanelAnimation) {
                    resetPanelMotion();
                }
            });
        }
    };

    const cancel = (message = null) => {
        revision += 1;
        cancelAnimations([entriesAnimation]);
        entriesAnimation = null;
        cancelAnimations(statusAnimations);
        statusAnimations = [];
        cancelAnimations([feedbackAnimation]);
        feedbackAnimation = null;
        resetPanelMotion();
        if (activeController instanceof AbortController) {
            activeController.abort();
        }
        activeController = null;
        if (debounceTimer !== null) {
            windowRef.clearTimeout(debounceTimer);
            debounceTimer = null;
        }
        section.removeAttribute('aria-busy');
        if (message !== null) {
            updateFeedback(message, message !== '');
        }
    };

    const clearFeedback = async (expectedMessage = null) => {
        if (expectedMessage !== null && feedback.textContent !== expectedMessage) {
            return;
        }

        cancel();
        if (!canAnimateElement(feedback, windowRef)) {
            setFeedback();

            return;
        }

        const styles = typeof globalThis.getComputedStyle === 'function'
            ? globalThis.getComputedStyle(feedback)
            : null;
        const naturalHeight = feedback.getBoundingClientRect?.().height
            || feedback.scrollHeight
            || 0;
        const marginBottom = styles?.getPropertyValue?.('margin-bottom')?.trim() || '0px';
        const animation = playAnimation(feedback, [
            {
                height: `${naturalHeight}px`,
                marginBottom,
                opacity: 1,
                overflow: 'hidden',
                transform: 'translateY(0)',
            },
            {
                height: '0px',
                marginBottom: '0px',
                opacity: 0,
                overflow: 'hidden',
                transform: 'translateY(-4px)',
            },
        ], {
            duration: readCssTimeMs(
                section,
                '--dictionary-results-swap-duration',
                160,
            ),
            easing: 'ease-in',
            fill: 'both',
        });
        if (animation === null) {
            setFeedback();

            return;
        }

        feedbackAnimation = animation;
        await settleAnimations([animation]);
        if (feedbackAnimation === animation) {
            animation.cancel();
            feedbackAnimation = null;
            feedback.textContent = '';
            feedback.hidden = true;
        }
    };

    const refreshResults = async (replacement) => {
        cancel('');
        const refreshRevision = revision;
        const currentEntries = section.querySelector('[data-dictionary-entries]');
        const currentSummary = section.querySelector('[data-dictionary-search-summary]');
        const replacementEntries = replacement?.querySelector?.('[data-dictionary-entries]');
        const replacementSummary = replacement?.querySelector?.('[data-dictionary-search-summary]');
        if (
            !(replacement instanceof HTMLElement)
            || !(currentEntries instanceof HTMLElement)
            || !(currentSummary instanceof HTMLElement)
            || !(replacementEntries instanceof HTMLElement)
            || !(replacementSummary instanceof HTMLElement)
        ) {
            return null;
        }

        const duration = readCssTimeMs(
            section,
            '--dictionary-results-swap-duration',
            160,
        );
        const outgoingAnimation = canAnimateElement(currentEntries, windowRef)
            && canAnimateElement(replacementEntries, windowRef)
            ? playAnimation(currentEntries, [
                { opacity: 1, transform: 'translateY(0)' },
                { opacity: 0, transform: 'translateY(-4px)' },
            ], {
                duration,
                easing: 'ease-in',
            })
            : null;
        const outgoingSummaryAnimation = canAnimateElement(currentSummary, windowRef)
            ? playAnimation(currentSummary, [
                { opacity: 1, transform: 'translateY(0)' },
                { opacity: 0, transform: 'translateY(-4px)' },
            ], {
                duration,
                easing: 'ease-in',
                fill: 'both',
            })
            : null;
        entriesAnimation = outgoingAnimation;
        statusAnimations = [outgoingSummaryAnimation].filter(Boolean);
        await settleAnimations([outgoingAnimation, outgoingSummaryAnimation]);
        if (refreshRevision !== revision) {
            return null;
        }

        animatePanelMutation(() => {
            replaceDictionaryResults(section, replacement);
        });
        cancelAnimations([outgoingSummaryAnimation]);
        const incomingAnimation = canAnimateElement(replacementEntries, windowRef)
            ? playAnimation(replacementEntries, [
                { opacity: 0, transform: 'translateY(4px)' },
                { opacity: 1, transform: 'translateY(0)' },
            ], {
                duration,
                easing: 'ease-out',
            })
            : null;
        const incomingSummaryAnimation = canAnimateElement(currentSummary, windowRef)
            ? playAnimation(currentSummary, [
                { opacity: 0, transform: 'translateY(4px)' },
                { opacity: 1, transform: 'translateY(0)' },
            ], {
                duration,
                easing: 'ease-out',
            })
            : null;
        entriesAnimation = incomingAnimation;
        statusAnimations = [incomingSummaryAnimation].filter(Boolean);
        const activePanelAnimation = panelAnimation;
        await settleAnimations([
            incomingAnimation,
            incomingSummaryAnimation,
            activePanelAnimation,
        ]);
        if (entriesAnimation === incomingAnimation) {
            entriesAnimation = null;
        }
        statusAnimations = [];
        if (panelAnimation === activePanelAnimation) {
            resetPanelMotion();
        }

        // The results are already swapped in: an animation cut short by a later cancel does not undo that,
        // and answering `null` here would have the caller insert a replacement emptied by the swap.
        return section;
    };

    const search = async ({ focusInput = false } = {}) => {
        if (hasDirtyRow(section)) {
            cancel(DIRTY_SEARCH_FEEDBACK);

            return false;
        }

        cancel();
        const requestRevision = revision;
        const query = input.value.trim();
        const requestUrl = new URL(fragmentUrl, windowRef.location.href);
        const currentUrl = new URL(windowRef.location.href);
        DICTIONARY_QUERY_PARAMS.forEach((parameter) => {
            const currentValue = currentUrl.searchParams.get(parameter);
            if (currentValue !== null && currentValue !== '') {
                requestUrl.searchParams.set(parameter, currentValue);
            }
        });
        if (query === '') {
            requestUrl.searchParams.delete(queryParam);
        } else {
            requestUrl.searchParams.set(queryParam, query);
        }
        requestUrl.searchParams.set('fragment', type);

        activeController = new AbortController();
        const requestController = activeController;
        const searchingPanelAnimation = animatePanelMutation(() => {
            section.setAttribute('aria-busy', 'true');
            setFeedback('Searching...');
        });
        if (searchingPanelAnimation !== null) {
            void settleAnimations([searchingPanelAnimation]).then(() => {
                if (panelAnimation === searchingPanelAnimation) {
                    resetPanelMotion();
                }
            });
        }

        try {
            const response = await fetchImpl(requestUrl.toString(), {
                headers: {
                    Accept: 'text/html',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                signal: requestController.signal,
            });
            if (!response.ok) {
                throw new Error(`Dictionary search failed with status ${response.status}`);
            }

            const html = await response.text();
            if (requestRevision !== revision || requestController.signal.aborted) {
                return false;
            }
            if (hasDirtyRow(section)) {
                cancel(DIRTY_SEARCH_FEEDBACK);

                return false;
            }

            const replacement = parseSection(html, type);
            const currentEntries = section.querySelector('[data-dictionary-entries]');
            const currentSummary = section.querySelector('[data-dictionary-search-summary]');
            const currentCount = section.querySelector('[data-dictionary-count]');
            const replacementEntries = replacement?.querySelector?.('[data-dictionary-entries]');
            const replacementSummary = replacement?.querySelector?.('[data-dictionary-search-summary]');
            const replacementCount = replacement?.querySelector?.('[data-dictionary-count]');
            const replacementInput = replacement?.querySelector?.('[data-dictionary-search-input]');
            if (
                !(replacement instanceof HTMLElement)
                || !(currentEntries instanceof HTMLElement)
                || !(currentSummary instanceof HTMLElement)
                || !(currentCount instanceof HTMLElement)
                || !(replacementEntries instanceof HTMLElement)
                || !(replacementSummary instanceof HTMLElement)
                || !(replacementCount instanceof HTMLElement)
                || !(replacementInput instanceof HTMLInputElement)
            ) {
                throw new Error('Invalid dictionary search response');
            }

            const canonicalQuery = replacementInput.value;
            const committedUrl = new URL(windowRef.location.href);
            if (canonicalQuery === '') {
                committedUrl.searchParams.delete(queryParam);
            } else {
                committedUrl.searchParams.set(queryParam, canonicalQuery);
            }

            const duration = readCssTimeMs(
                section,
                '--dictionary-results-swap-duration',
                160,
            );
            const outgoingAnimation = canAnimateElement(currentEntries, windowRef)
                && canAnimateElement(replacementEntries, windowRef)
                ? playAnimation(currentEntries, [
                    { opacity: 1, transform: 'translateY(0)' },
                    { opacity: 0, transform: 'translateY(-4px)' },
                ], {
                    duration,
                    easing: 'ease-in',
                })
                : null;
            const outgoingStatusAnimations = [currentSummary, feedback]
                .filter((element) => !element.hidden && canAnimateElement(element, windowRef))
                .map((element) => playAnimation(element, [
                    { opacity: 1, transform: 'translateY(0)' },
                    { opacity: 0, transform: 'translateY(-4px)' },
                ], {
                    duration,
                    easing: 'ease-in',
                    fill: 'both',
                }))
                .filter(Boolean);
            entriesAnimation = outgoingAnimation;
            statusAnimations = outgoingStatusAnimations;
            if (outgoingAnimation !== null || outgoingStatusAnimations.length > 0) {
                await settleAnimations([outgoingAnimation, ...outgoingStatusAnimations]);
                if (entriesAnimation === outgoingAnimation) {
                    entriesAnimation = null;
                }
                if (requestRevision !== revision || requestController.signal.aborted) {
                    return false;
                }
                if (hasDirtyRow(section)) {
                    cancel(DIRTY_SEARCH_FEEDBACK);

                    return false;
                }
            }

            animatePanelMutation(() => {
                replaceDictionaryResults(section, replacement);
                if (input.value !== canonicalQuery) {
                    input.value = canonicalQuery;
                }
                clearButton.hidden = canonicalQuery === '';
                setFeedback();
            });
            cancelAnimations(outgoingStatusAnimations);
            if (statusAnimations === outgoingStatusAnimations) {
                statusAnimations = [];
            }
            windowRef.history?.replaceState?.(
                windowRef.history?.state ?? null,
                '',
                `${committedUrl.pathname}${committedUrl.search}${committedUrl.hash}`,
            );

            const incomingAnimation = canAnimateElement(replacementEntries, windowRef)
                ? playAnimation(replacementEntries, [
                    { opacity: 0, transform: 'translateY(4px)' },
                    { opacity: 1, transform: 'translateY(0)' },
                ], {
                    duration,
                    easing: 'ease-out',
                })
                : null;
            const incomingStatusAnimations = canAnimateElement(currentSummary, windowRef)
                ? [playAnimation(currentSummary, [
                    { opacity: 0, transform: 'translateY(4px)' },
                    { opacity: 1, transform: 'translateY(0)' },
                ], {
                    duration,
                    easing: 'ease-out',
                })].filter(Boolean)
                : [];
            entriesAnimation = incomingAnimation;
            statusAnimations = incomingStatusAnimations;
            const activePanelAnimation = panelAnimation;
            await settleAnimations([
                incomingAnimation,
                activePanelAnimation,
                ...incomingStatusAnimations,
            ]);
            if (entriesAnimation === incomingAnimation) {
                entriesAnimation = null;
            }
            if (statusAnimations === incomingStatusAnimations) {
                statusAnimations = [];
            }
            if (panelAnimation === activePanelAnimation) {
                resetPanelMotion();
            }
            if (requestRevision !== revision || requestController.signal.aborted) {
                return false;
            }
            if (focusInput) {
                input.focus();
                input.setSelectionRange(input.value.length, input.value.length);
            }

            return true;
        } catch (error) {
            cancelAnimations(statusAnimations);
            statusAnimations = [];
            if (!requestController.signal.aborted && requestRevision === revision) {
                setFeedback('Search failed. Try again.');
            }

            return false;
        } finally {
            if (activeController === requestController) {
                activeController = null;
                section.removeAttribute('aria-busy');
            }
        }
    };

    const scheduleSearch = (event) => {
        if (event.isComposing) {
            return;
        }

        clearButton.hidden = input.value === '';
        if (hasDirtyRow(section)) {
            cancel(DIRTY_SEARCH_FEEDBACK);

            return;
        }

        animatePanelMutation(() => {
            cancel();
            setFeedback();
        });
        debounceTimer = windowRef.setTimeout(() => {
            debounceTimer = null;
            void search();
        }, SEARCH_DEBOUNCE_MS);
    };

    form.addEventListener('submit', (event) => {
        event.preventDefault();
        void search({ focusInput: true });
    });
    input.addEventListener('input', scheduleSearch);
    input.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
            return;
        }

        event.preventDefault();
        input.value = '';
        clearButton.hidden = true;
        void search({ focusInput: true });
    });
    clearButton.addEventListener('click', () => {
        input.value = '';
        clearButton.hidden = true;
        void search({ focusInput: true });
    });

    return { cancel, clearFeedback, refreshResults, search };
}
